import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type MouseEvent } from 'react';
import { getBootstrapData } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import { getMediaDisplayUrl, type MediaPayload } from '@/hub-shared/utils/mediaPayload';
import { AddItemIcon } from './AddItemIcon';

type UploadState = 'idle' | 'uploading' | 'error';

type MediaUploaderProps = {
  value?: number;
  media?: MediaPayload | null;
  onChange: (attachmentId: number | undefined) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  bootstrapKey?: string;
  uploadEndpoint?: string;
};

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MediaUploader = ({
  value,
  media,
  onChange,
  label,
  description,
  disabled = false,
  bootstrapKey = 'AccountingHubCompaniesAdmin',
  uploadEndpoint = 'companies/upload-image',
}: MediaUploaderProps) => {
  const mediaUrl = getMediaDisplayUrl(media);
  const [imageUrl, setImageUrl] = useState(mediaUrl);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastLocalUrlRef = useRef('');

  useEffect(() => {
    if (lastLocalUrlRef.current && imageUrl) {
      return;
    }
    setImageUrl(mediaUrl);
    if (!mediaUrl) {
      lastLocalUrlRef.current = '';
    }
  // imageUrl omitted — feedback loop
  }, [value, mediaUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const processFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setErrorMsg(__('Veuillez sélectionner un fichier image.'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg(__('Le fichier est trop volumineux. Maximum 5 Mo.'));
      return;
    }

    setErrorMsg('');

    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result) {
        const dataUrl = ev.target.result as string;
        setImageUrl(dataUrl);
        lastLocalUrlRef.current = dataUrl;
      }
    };
    reader.readAsDataURL(file);

    setUploadState('uploading');
    try {
      const bootstrap = getBootstrapData(bootstrapKey);
      const body = new FormData();
      body.append('file', file);

      const response = await fetch(`${bootstrap.restUrl}${uploadEndpoint}`, {
        method: 'POST',
        headers: { 'X-WP-Nonce': bootstrap.nonce },
        credentials: 'same-origin',
        body,
      });

      if (!response.ok) {
        if (response.status === 413) {
          throw new Error(
            __(
              'Ce fichier est trop volumineux pour le serveur. Réessayez avec une image plus légère (max. 5 Mo).',
            ),
          );
        }
        const err = await response.json().catch(() => ({}));
        const m = (err as { message?: string }).message?.trim();
        if (m && !/^HTTP\s*\d{3}$/i.test(m) && !/^Upload failed:\s*\d{3}$/i.test(m)) {
          throw new Error(m);
        }
        throw new Error(__('Le téléversement a échoué.') + ` (${response.status})`);
      }

      const attachment = await response.json() as { id?: number; media?: MediaPayload };
      if (attachment.id) {
        onChange(attachment.id);
        const uploadedUrl = getMediaDisplayUrl(attachment.media);
        setImageUrl(uploadedUrl);
        lastLocalUrlRef.current = uploadedUrl;
      }
      setUploadState('idle');
    } catch (err) {
      setUploadState('error');
      setErrorMsg(err instanceof Error ? err.message : __("Erreur lors du téléversement de l'image."));
      setImageUrl('');
      lastLocalUrlRef.current = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void processFile(file);
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || uploadState === 'uploading') return;
    const file = e.dataTransfer.files[0];
    if (file) void processFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!disabled && uploadState !== 'uploading') setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const openPicker = () => {
    if (!disabled && uploadState !== 'uploading') fileInputRef.current?.click();
  };

  const removeImage = (e: MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setImageUrl('');
    setErrorMsg('');
    lastLocalUrlRef.current = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadState('idle');
  };

  const isUploading = uploadState === 'uploading';
  const isDisabled = disabled || isUploading;

  return (
    <div className="form-field">
      {label && <label>{label}</label>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="visually-hidden"
        onChange={handleFileSelect}
        disabled={isDisabled}
        aria-label={label}
      />

      {imageUrl ? (
        <div className="media-uploader-preview">
          <img src={imageUrl} alt="" className="media-uploader-preview__img" />
          <div className="media-uploader-preview__overlay">
            <button
              type="button"
              className="btn-secondary media-uploader-preview__replace"
              onClick={openPicker}
              disabled={isDisabled}
            >
              {isUploading ? __('Téléversement…') : __('Remplacer')}
            </button>
            <button
              type="button"
              className="media-uploader-preview__remove"
              onClick={removeImage}
              disabled={isDisabled}
                aria-label={__("Supprimer l'image")}
            >
              <TrashIcon />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`dropzone media-uploader-dropzone${dragging ? ' dragging' : ''}${isDisabled ? ' disabled' : ''}`}
          onClick={openPicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          disabled={isDisabled}
          aria-busy={isUploading}
        >
          <div className="dropzone-content">
            <span className="dropzone-icon">
              <AddItemIcon />
            </span>
            <span className="dropzone-text">
              <span className="dropzone-primary">
                {isUploading ? __('Téléversement…') : __('Déposer ou choisir une image')}
              </span>
              {!isUploading && (
                <span className="dropzone-secondary media-uploader-dropzone__hint">
                  {__('PNG, JPG, SVG · max 5 Mo')}
                </span>
              )}
            </span>
          </div>
        </button>
      )}

      {errorMsg && <p className="media-uploader__error">{errorMsg}</p>}
      {description && <p className="description">{description}</p>}
    </div>
  );
};

export default MediaUploader;
